import { cn } from '@workingclock/ui';
import type { ReactNode } from 'react';

/**
 * A titled "code surface" frame, flat-by-default like the device frames in
 * home/device-frames.tsx (neutral chrome dots, borders only, no shadow at
 * rest — DESIGN.md's Flat-By-Default Rule). Used on the developers page to
 * show real terminal sessions and MCP config. The body scrolls horizontally
 * on its own rather than letting a long command line push the page wide.
 */
export function CodeWindow({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('overflow-hidden rounded-md border border-border bg-surface', className)}>
      <div className="flex items-center gap-1.5 border-b border-border px-3 py-2.5">
        <span className="h-2 w-2 rounded-full bg-border" />
        <span className="h-2 w-2 rounded-full bg-border" />
        <span className="h-2 w-2 rounded-full bg-border" />
        <span className="ml-2 font-mono text-label text-muted">{title}</span>
      </div>
      <div className="overflow-x-auto p-4">
        <pre className="font-mono text-label leading-relaxed text-ink">{children}</pre>
      </div>
    </div>
  );
}

/** A shell command line: a gold prompt glyph followed by the command in ink. */
export function CommandLine({ children }: { children: ReactNode }) {
  return (
    <div className="whitespace-pre text-ink">
      <span className="select-none text-gold" aria-hidden>
        ${' '}
      </span>
      {children}
    </div>
  );
}

/** Program output or a comment line, printed in muted tone under a command. */
export function OutputLine({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('whitespace-pre text-muted', className)}>{children}</div>;
}
